import { useQuerySessionChats } from '@/hooks/query/useQuerySessionChats';
import { useQuerySessionById } from '@heidi/session/data';
import { useMemo } from 'react';

export const FRAGMENT_CHATS_LIMIT = 3;

type ConsultNoteFragment = {
  /** Display label — the note's heading, or null to use the intl fallback. */
  heading: string | null;
};

/**
 * Lazily fetched fragments for one expanded visit: the consult note (from the
 * session detail — one GET, the same one the session page needs, so a hover
 * prefetch makes it free) and the session's Ask-Heidi threads (one GET).
 * Mount this hook only while the cluster is expanded.
 */
export function useSessionFragments(sessionId: string) {
  const sessionQuery = useQuerySessionById({
    sessionId,
    select: (session) => ({
      hasGeneratedNote: Boolean(session?.consult_note?.result),
      noteHeading: session?.consult_note?.heading ?? null,
    }),
  });

  const chatsQuery = useQuerySessionChats(sessionId);

  const note: ConsultNoteFragment | null = sessionQuery.data?.hasGeneratedNote
    ? { heading: sessionQuery.data.noteHeading }
    : null;

  const chats = useMemo(
    () => (chatsQuery.data?.chats ?? []).slice(0, FRAGMENT_CHATS_LIMIT),
    [chatsQuery.data?.chats]
  );

  return {
    note,
    chats,
    isLoading: sessionQuery.isLoading || chatsQuery.isLoading,
  };
}
